import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, Upload, Menu, HelpCircle, User, FileText } from 'lucide-react';
import { useCRM } from '../utils/CRMContext';
import { Lead, LeadStatus } from '../types';
import AppHeader from '../components/ui/AppHeader';
import LeadsSidebar, { LeadsSidebarView } from '../components/leads/LeadsSidebar';
import LeadsTable from '../components/leads/LeadsTable';
import HubQuickSearch from '../components/search/HubQuickSearch';
import QuickAddLeadModal from '../components/leads/QuickAddLeadModal';
import SamplePortalInvitesModal from '../components/leads/SamplePortalInvitesModal';
import LeadsHubHelpModal from '../components/leads/LeadsHubHelpModal';
import { useLeadsHubTour } from '../components/leads/LeadsHubTour';
import { useLeadLastUpdated } from '../hooks/useLeadLastUpdated';
import { useActivities } from '../hooks/useActivities';
import { useHubQuickSearchHotkey } from '../hooks/useHubQuickSearchHotkey';
import { searchLeadsHub } from '../utils/hubSearch/searchLeadsHub';
import { HubSearchResult } from '../types/hubSearch';
import { LEAD_STATUSES_EXCLUDED_FROM_ALL } from '../components/leads/leadStatusDisplay';
import {
  buildHasLoggedCallByLead,
  buildNextActivityByLead,
  countLeadsNeedingFollowUp,
  filterAndSortLeads,
  isHotLead,
  LeadsDateRange,
  LeadsFilterState,
  LeadsSortColumn,
} from '../utils/leadsHubUtils';
import '../components/pm/pm-shared.css';
import './OperationsHub.css';
import './LeadsHub.css';

const LeadsHub: React.FC = () => {
  const { activities } = useActivities();
  const { leads, contacts, deals, loading, bulkUpdateLeads, bulkDeleteLeads, bulkArchiveLeads, crmUsers, updateLead } = useCRM();
  const navigate = useNavigate();
  const location = useLocation();
  const lastUpdatedByLead = useLeadLastUpdated(activities);

  const initialState = location.state as {
    statusFilter?: LeadStatus;
    sourceFilter?: LeadsFilterState['sourceFilter'];
    dateRange?: LeadsDateRange;
  } | null;

  const [sidebarView, setSidebarView] = useState<LeadsSidebarView>(
    initialState?.statusFilter || 'all',
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRangeFilter, setDateRangeFilter] = useState<LeadsDateRange>(initialState?.dateRange || 'all');
  const [sourceFilter, setSourceFilter] = useState<LeadsFilterState['sourceFilter']>(
    initialState?.sourceFilter || 'all',
  );
  const [showArchived, setShowArchived] = useState(false);
  const [sortBy, setSortBy] = useState<LeadsSortColumn>('lastUpdated');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showSampleInviteModal, setShowSampleInviteModal] = useState(false);
  const [showQuickSearch, setShowQuickSearch] = useState(false);

  const { startTour } = useLeadsHubTour(!loading);

  useEffect(() => {
    if (initialState?.statusFilter || initialState?.sourceFilter || initialState?.dateRange) {
      window.history.replaceState({}, document.title);
    }
  }, [initialState]);

  useEffect(() => {
    const handleSelectLead = (event: CustomEvent<{ leadId: string }>) => {
      if (event.detail?.leadId) {
        navigate(`/client/${event.detail.leadId}`);
      }
    };
    window.addEventListener('selectLead', handleSelectLead as EventListener);
    return () => window.removeEventListener('selectLead', handleSelectLead as EventListener);
  }, [navigate]);

  useHubQuickSearchHotkey({
    onOpen: () => setShowQuickSearch(true),
    blocked: showQuickSearch || showHelpModal || showAddLeadModal,
  });

  const getQuickSearchResults = useCallback(
    (query: string) =>
      searchLeadsHub({
        query,
        leads,
        contacts,
        deals,
        activities,
        crmUsers,
      }),
    [leads, contacts, deals, activities, crmUsers],
  );

  const handleQuickSearchSelect = useCallback(
    (result: HubSearchResult) => {
      if (result.kind === 'lead') {
        navigate(`/client/${result.id}`);
      } else if (result.kind === 'contact') {
        navigate(`/clients?contact=${result.id}`);
      } else if (result.kind === 'deal') {
        const deal = result.payload as { leadId?: string };
        navigate(`/client/${deal.leadId || ''}`);
      }
    },
    [navigate],
  );

  const nextActivityByLead = useMemo(
    () => buildNextActivityByLead(leads, activities),
    [leads, activities],
  );
  const hasLoggedCallByLead = useMemo(
    () => buildHasLoggedCallByLead(leads, activities),
    [leads, activities],
  );

  const activeLeads = useMemo(() => leads.filter(l => !l.archived), [leads]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: 0, hot: 0 };
    activeLeads.forEach(lead => {
      if (isHotLead(lead)) counts.hot += 1;
      counts[lead.status] = (counts[lead.status] || 0) + 1;
      if (!LEAD_STATUSES_EXCLUDED_FROM_ALL.includes(lead.status)) counts.all += 1;
    });
    return counts;
  }, [activeLeads]);

  const filterState: LeadsFilterState = useMemo(
    () => ({
      searchQuery,
      statusFilter:
        sidebarView === 'hot' || sidebarView === 'all'
          ? 'all'
          : (sidebarView as LeadStatus),
      sourceFilter,
      dateRangeFilter,
      showArchived,
      hotLeadsOnly: sidebarView === 'hot',
      ownerFilter: 'all',
    }),
    [searchQuery, sidebarView, sourceFilter, dateRangeFilter, showArchived],
  );

  const filteredLeads = useMemo(
    () =>
      filterAndSortLeads(
        leads,
        filterState,
        sortBy,
        sortOrder,
        nextActivityByLead,
        lastUpdatedByLead,
        crmUsers,
      ),
    [leads, filterState, sortBy, sortOrder, nextActivityByLead, lastUpdatedByLead, crmUsers],
  );

  const needsFollowUp = useMemo(
    () => countLeadsNeedingFollowUp(activeLeads, nextActivityByLead),
    [activeLeads, nextActivityByLead],
  );

  const handleSort = (column: LeadsSortColumn) => {
    if (sortBy === column) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder(column === 'name' ? 'asc' : 'desc');
    }
  };

  const handleSidebarView = (view: LeadsSidebarView) => {
    setSidebarView(view);
    setSidebarOpen(false);
  };

  const toggleSelect = (leadId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const visibleIds = filteredLeads.map(l => l.id).filter((id): id is string => !!id);
    if (selectedIds.size === visibleIds.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visibleIds));
    }
  };

  const handleBulkStatus = useCallback(
    async (status: LeadStatus) => {
      if (selectedIds.size === 0) return;
      await bulkUpdateLeads(Array.from(selectedIds), { status });
      setSelectedIds(new Set());
    },
    [selectedIds, bulkUpdateLeads],
  );

  const handleLeadStatusChange = useCallback(
    async (leadId: string, status: LeadStatus) => {
      await updateLead(leadId, { status });
    },
    [updateLead],
  );

  const handleBulkArchive = useCallback(
    async (archive: boolean) => {
      if (selectedIds.size === 0) return;
      await bulkArchiveLeads(Array.from(selectedIds), archive);
      setSelectedIds(new Set());
    },
    [selectedIds, bulkArchiveLeads],
  );

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return;
    const msg = `Delete ${selectedIds.size} lead(s)? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    await bulkDeleteLeads(Array.from(selectedIds));
    setSelectedIds(new Set());
  }, [selectedIds, bulkDeleteLeads]);

  if (loading) {
    return (
      <div className="ops-hub">
        <AppHeader />
        <div className="ops-hub-loading">
          <div className="ops-hub-loading-bar" />
        </div>
      </div>
    );
  }

  return (
    <div className="ops-hub leads-hub">
      <AppHeader />
      <div className="ops-hub-layout leads-hub-layout">
        {sidebarOpen && (
          <button
            type="button"
            className="leads-hub-sidebar-backdrop"
            aria-label="Close filters"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        <LeadsSidebar
          isOpen={sidebarOpen}
          activeView={sidebarView}
          onSelectView={handleSidebarView}
          statusCounts={statusCounts}
          dateRange={dateRangeFilter}
          onDateRangeChange={setDateRangeFilter}
          sourceFilter={sourceFilter}
          onSourceFilterChange={setSourceFilter}
          showArchived={showArchived}
          onToggleShowArchived={() => setShowArchived(v => !v)}
        />

        <div className="ops-hub-main">
          <div className="ops-hub-toolbar">
            <div className="ops-hub-toolbar-left">
              <button
                type="button"
                className="leads-hub-menu-btn"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open filters"
              >
                <Menu size={18} />
              </button>
              <h1 className="ops-hub-title">Leads</h1>
              <div className="ops-hub-stats" data-tour="leads-stats">
                <span className="ops-stat">{activeLeads.length} total</span>
                <span className="ops-stat ops-stat-active">{statusCounts.new || 0} new</span>
                <span className="ops-stat ops-stat-overdue">{needsFollowUp} follow-up</span>
                <span className="ops-stat ops-stat-done">{statusCounts.qualified || 0} qualified</span>
              </div>
            </div>

            <div className="leads-hub-toolbar-search" data-tour="leads-search">
              <div className="leads-hub-search-wrap">
                <Search size={15} className="leads-hub-search-icon" />
                <input
                  type="search"
                  className="leads-hub-search"
                  placeholder="Filter list…"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  aria-label="Filter leads in table"
                />
              </div>
            </div>

            <div className="leads-hub-toolbar-actions">
              <button
                type="button"
                className="leads-hub-secondary-btn"
                onClick={() => setShowQuickSearch(true)}
                data-tour="leads-quick-search-btn"
              >
                <Search size={15} /> Quick search
              </button>
              <button
                type="button"
                className="leads-hub-secondary-btn"
                onClick={() => setShowHelpModal(true)}
                data-tour="leads-help-btn"
                aria-label="Help"
              >
                <HelpCircle size={15} /> Help
              </button>
              <button
                type="button"
                className="leads-hub-secondary-btn"
                onClick={() => setShowSampleInviteModal(true)}
                title="Manage sample proposal invites for prospective GCs"
                data-tour="leads-sample-invite-btn"
              >
                <FileText size={15} /> Sample invite
              </button>
              <button
                type="button"
                className="leads-hub-secondary-btn"
                onClick={() => navigate('/import')}
              >
                <Upload size={15} /> Import
              </button>
              <button
                type="button"
                className="ops-new-btn"
                onClick={() => setShowAddLeadModal(true)}
                data-tour="leads-new-btn"
              >
                + New Lead
              </button>
            </div>
          </div>

          <div className="leads-hub-content">
            <LeadsTable
              leads={filteredLeads}
              totalCount={leads.length}
              crmUsers={crmUsers}
              lastUpdatedByLead={lastUpdatedByLead}
              hasLoggedCallByLead={hasLoggedCallByLead}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onToggleSelectAll={toggleSelectAll}
              onClearSelection={() => setSelectedIds(new Set())}
              onBulkStatus={handleBulkStatus}
              onLeadStatusChange={handleLeadStatusChange}
              onBulkArchive={handleBulkArchive}
              onBulkDelete={handleBulkDelete}
            />
          </div>
        </div>
      </div>

      <HubQuickSearch
        open={showQuickSearch}
        onClose={() => setShowQuickSearch(false)}
        getResults={getQuickSearchResults}
        onSelect={handleQuickSearchSelect}
        placeholder="Search leads, contacts, deals…"
        ariaLabel="Leads quick search"
        hint={<>Press <kbd>Space</kbd> anywhere on this page to open quick search</>}
        renderIcon={() => <User size={16} />}
      />
      {showAddLeadModal && (
        <QuickAddLeadModal
          isOpen={showAddLeadModal}
          onClose={() => setShowAddLeadModal(false)}
          onLeadCreated={leadId => {
            setShowAddLeadModal(false);
            navigate(`/client/${leadId}`);
          }}
        />
      )}
      {showHelpModal && (
        <LeadsHubHelpModal
          onClose={() => setShowHelpModal(false)}
          onTakeTour={() => {
            setShowHelpModal(false);
            startTour();
          }}
        />
      )}
      <SamplePortalInvitesModal
        open={showSampleInviteModal}
        onClose={() => setShowSampleInviteModal(false)}
      />
    </div>
  );
};

export default LeadsHub;
